import {
  BAND_THRESHOLDS,
  HEALTH_CHECK_QUESTIONS,
  type Band,
  type OptionId,
  type QuestionId,
} from '../content/healthCheck';

/**
 * The Health Check as a pure state machine: intro → questions → capture →
 * done. Answers live here and nowhere else — never serialised, never sent
 * (src/types/waitlist.ts forbids clinical data pre-account; these are not
 * clinical, but the rule is applied wholesale).
 */

export type Phase = 'intro' | 'questions' | 'capture' | 'done';

export type Answers = Partial<Record<QuestionId, OptionId>>;

export interface HcState {
  phase: Phase;
  /** Index into the question list while phase === 'questions'. */
  index: number;
  answers: Answers;
  /** The waitlist entry created at capture, so the summary can offer the deposit. */
  entryRef: string | null;
}

export type HcAction =
  | { type: 'START' }
  | { type: 'ANSWER'; questionId: QuestionId; optionId: OptionId }
  | { type: 'NEXT' }
  | { type: 'BACK' }
  | { type: 'CAPTURED'; ref: string | null }
  | { type: 'RESET' };

export const initialState: HcState = { phase: 'intro', index: 0, answers: {}, entryRef: null };

export function reduce(state: HcState, action: HcAction, total = HEALTH_CHECK_QUESTIONS.length): HcState {
  switch (action.type) {
    case 'START':
      return state.phase === 'intro' ? { ...state, phase: 'questions', index: 0 } : state;
    case 'ANSWER':
      if (state.phase !== 'questions') return state;
      return { ...state, answers: { ...state.answers, [action.questionId]: action.optionId } };
    case 'NEXT': {
      if (state.phase !== 'questions') return state;
      const current = HEALTH_CHECK_QUESTIONS[state.index];
      if (current && !state.answers[current.id]) return state; // nothing chosen yet
      if (state.index + 1 >= total) return { ...state, phase: 'capture' };
      return { ...state, index: state.index + 1 };
    }
    case 'BACK':
      if (state.phase === 'questions') {
        return state.index === 0 ? { ...state, phase: 'intro' } : { ...state, index: state.index - 1 };
      }
      if (state.phase === 'capture') return { ...state, phase: 'questions', index: total - 1 };
      return state;
    case 'CAPTURED':
      return state.phase === 'capture' ? { ...state, phase: 'done', entryRef: action.ref } : state;
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export function score(answers: Answers): { score: number; max: number } {
  let total = 0;
  let max = 0;
  for (const question of HEALTH_CHECK_QUESTIONS) {
    max += 2;
    const chosen = answers[question.id];
    const option = question.options.find((o) => o.id === chosen);
    if (option) total += option.weight;
  }
  return { score: total, max };
}

export function summarise(answers: Answers): { band: Band; score: number; max: number } {
  const { score: total, max } = score(answers);
  const band = BAND_THRESHOLDS.find((t) => total >= t.min)?.band ?? 'unmanaged';
  return { band, score: total, max };
}
